import { useEffect, useState } from "react";
import { Account } from "@/types/Account";
import PageHeader from "@/components/molecules/PageHeader";
import Container from "@/components/atoms/Container";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import axios from "axios";
import User from "@/types/User";
import Log from "@/types/Log";
import LogFilter from "@/components/organisms/Log/Filter";
import Pagination from "@/components/atoms/Pagination";
import { router } from "@inertiajs/react";
import EditLog from "@/components/organisms/Log/EditLog";
import { SquarePen } from "lucide-react";
import useAccess from "@/hooks/useAccess";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/molecules/Sheet";
import LogCategory from "@/types/LogCategory";
import Spinner from '@/components/atoms/Spinner';
import { X } from "lucide-react";

type Props = {
  accounts: Account[];
  users: User[];
};

interface Filters {
  account_ids?: number[];
  search?: string;
  user_ids?: number[];
  sow_ids?: number;
  country_ids?: number[];
  start_date?: string;
  end_date?: string;
}



const Index = ({ accounts, users }: Props) => {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [logCategories, setLogCategories] = useState<LogCategory[]>([]);
  const { getAccess } = useAccess();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedLogFormVariables, setSelectedLogFormVariables] = useState<any | null>(null);
  const [exportMode, setExportMode] = useState<boolean>(false);
  const [exportButtonText, setExportButtonText] = useState<string>("Export");
  const [loading, setLoading] = useState(false);
  const [totalDuration, setTotalDuration] = useState("00:00:00");
  const [sorting, setSorting] = useState<{ column: string; direction: string | undefined }>({ column: "", direction: undefined });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedSows, setSelectedSows] = useState<string[]>([]);
  const columns: ColumnDef<Log>[] = [
    {
        id: "ticketId",
        accessorKey: "ticket.displayid",
        header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>ID</ColumnHeader>,
        cell: ({ row }) => {
          const log = row.original;
          const url = `/tickets/${log.ticket.id}`;
          const ticketId = log.ticket.displayid;
          return (
            <a
              href={url}
              target="_blank"
              className="text-blue-500 hover:underline"
            >
              {ticketId}
            </a>
          );
        },
    },
    {
        id: "ticketSubject",
        accessorKey: "ticket.subject",
        header: ({ column }) => <ColumnHeader column={column}>Subject</ColumnHeader>,
    },
    {
        id: "logOwner",
        accessorKey: "log_owner.name",
        header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>User</ColumnHeader>,
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <img
              src={row.original.log_owner.avatar_url || "/default_avatar.png"}
              alt={row.original.log_owner.name}
              className="w-8 h-8 rounded-full dark:bg-darkTextColor"
            />
            <span>{row.original.log_owner.name}</span>
          </div>
        ),
    },
    {
        id: "account",
        accessorKey: "account.name",
        header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Account</ColumnHeader>,
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <img
              src={row.original.account.avatar_thumb || "/default_avatar.png"}
              alt={row.original.account.name}
              className="w-8 h-8 rounded-full dark:bg-darkTextColor"
            />
            <span>{row.original.account.name}</span>
          </div>
        ),
    },
    {
      id: "ticketCountry",
      accessorKey: "ticket.countries",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Market</ColumnHeader>,
      cell: ({ row }) => {
        const countries = row.original.ticket?.countries || [];
        const countryNames = countries.map((country: any) => country.name).join(", ");
        return <span>{countryNames || "N/A"}</span>;
      },
    },
    {
      id: "duration",
      accessorKey: "duration",
      header: ({ column }) => <ColumnHeader column={column}>Duration</ColumnHeader>,
    },
    {
        id: "startedAt",
        accessorKey: "started_at",
        header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Started At</ColumnHeader>,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (row: any) => (
        <SquarePen
          className="cursor-pointer"
          size={20}
          style={{ opacity: (!getAccess(["create_manual_log"]) && !getAccess(["create_log_for_others"])) ? 0.5 : 1, pointerEvents: !getAccess(["create_manual_log"]) && !getAccess(["create_log_for_others"]) ? 'none' : 'auto' }}
          onClick={() => {
            const data = {
              ticket: {
                id: row.row.original.ticket.id,
                displayId: row.row.original.ticket.displayid,
                subject: row.row.original.ticket.subject,
              },
              logCategories: row.row.original.ticket.displayid === 'Non-Chargeable' ? logCategories.filter((category: any) => category.kind === "Non-Chargeable") : logCategories.filter((category: any) => category.kind === "Chargeable"),
              internalSubTicketTypes: row.row.original.ticket.internal_sub_ticket_types,
              log: row.row.original,
            }
            
            setSelectedLogFormVariables(data);
            setIsEditOpen(true);
          }}
        />
      ),
      size: 20,
    },
  ];

  const handleSortChange = (column: string, direction: string | undefined) => {
    setSorting({ column, direction });
  };

  const handleFilterChange = (filterId: string, value: any) => {
    if (filterId === 'account_ids') {
      setSelectedAccounts(value as string[]);
    }

    if (filterId === 'user_ids') {
      setSelectedUsers(value as string[]);
    }

    if (filterId === 'sow_ids') {
      setSelectedSows(value as string[]);
    }

    setFilters(prev => ({ ...prev, [filterId]: value }));
  };

  const handlePageChange = (page: number) => {  
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchLogs = async () => {
    if (Object.keys(filters).length === 0) {
      return;
    }

    const queryParams: Record<string, any> = {
      ...filters,
      ...pagination,
      column: sorting.column,
      direction: sorting.direction,
    };

    setLoading(true);

    const response = await axios.get('/logs/listing', {
      params: queryParams
    });

    setLogs(response.data.logs);
    fetchLogCategories();
    setTotalCount(response.data.total_count);
    setTotalPages(response.data.total_pages);
    setTotalDuration(response.data.total_duration);

    if (response.data.current_page !== pagination.page) {
      setPagination(prev => ({ ...prev, page: response.data.current_page }));
    }

    setLoading(false);
  };

  const fetchLogCategories = async () => {
    try {
      const logCategoryResponse = await axios.get("/log_categories/listing");
      setLogCategories(logCategoryResponse.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (logs.length > 0) {
      fetchLogs();
      fetchLogCategories();
    }
  }, [pagination, sorting]);

  const breadcrumbItems = [
    {
      label: "Timesheet",
      onClick: () => router.get("/logs"),
    },
  ];

  const handleExport = () => {
    const exportLogs = async () => {
      setExportMode(true);
      const queryParams: Record<string, any> = {
        ...filters
      };

      await axios.get('/logs/export', {
        params: queryParams
      }).then(response => {
        if (response.status === 200 && response.data && response.data.jid) {
          const jobId = response.data.jid;

          setExportButtonText('Exporting...');
          checkExportStatus(jobId);
        }
      })
        .catch(error => {
          console.error('Error exporting data:', error);
        });
    };

    exportLogs();
  }

  const checkExportStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`/logs/export_status/${jobId}`);

      if (response.data.status === 'complete') {
        setExportMode(false);
        setExportButtonText('Export Table');
        window.location.href = `/logs/export_download/${jobId}`;
      } else if (response.data.status === 'failed') {
        setExportMode(false);
        setExportButtonText('Export Table');
        alert('Export failed. Please contact administrator.');
      } else {
        if (response.data.percentage) {
          setExportButtonText(`Exporting... ${response.data.percentage}%`);
        }

        setTimeout(() => checkExportStatus(jobId), 1000);
      }
    } catch (error) {
      console.error('Error checking export status:', error);
    }
  };

  const removeAccountTag = (value: string) => {
    setSelectedAccounts(selectedAccounts.filter((item) => item !== value));
  };

  const removeUserTag = (value: string) => {
    setSelectedUsers(selectedUsers.filter((item) => item !== value));
  };

  const removeSowTag = (value: string) => {
    setSelectedSows(selectedSows.filter((item) => item !== value));
  };

  return (
    <>
      <Container>
      {loading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <Spinner />
          </div>
        )}
        <PageHeader title='Timesheet' breadcrumbItems={breadcrumbItems}/>
        <div className="flex flex-wrap gap-2 mt-4 mb-4 ">
          {selectedAccounts && selectedAccounts.length > 0 && (
            <span className="font-semibold text-gray-700 dark:text-darkTextColor">Accounts:</span>
          )}

          {selectedAccounts && (
            selectedAccounts.map((item:any) => (
              <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
                {accounts.find((opt) => opt.id.toString() == item)?.name}
                <button onClick={() => removeAccountTag(item)}>
                  <X size={16} />
                </button>
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-4 mb-4 ">
          {selectedUsers && selectedUsers.length > 0 && users.length > 1 && (
            <span className="font-semibold text-gray-700 dark:text-darkTextColor">Users:</span>
          )}
          {selectedUsers && users.length > 1 && (
            selectedUsers.map((item:any) => (
              <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
                {users.find((opt) => opt.id.toString() == item)?.name}
                <button onClick={() => removeUserTag(item)}>
                  <X size={16} />
                </button>
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-4 mb-4 ">
          {selectedSows && selectedSows.length > 0 && (
            <span className="font-semibold text-gray-700 dark:text-darkTextColor">Sows:</span>
          )}
          {selectedSows && (
            selectedSows.map((item:any) => (
              <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
                {
                  accounts
                    .filter((account: any) => selectedAccounts.includes(account.id.toString()))
                    .flatMap((account: any) => account.sows)
                    .find((sow: any) => sow.id.toString() === item)?.display_id
                }
                <button onClick={() => removeSowTag(item)}>
                  <X size={16} />
                </button>
              </span>
            ))
          )}
        </div>
        <LogFilter 
          filters={[
            {
              id: "user_ids",
              label: "User(s)",
              options: users.map(user => ({ value: user.id, label: user.name})),
              selected: selectedUsers
            },
            {
              id: "account_ids",
              label: "Account(s)",
              options: accounts.map(account => ({ value: account.id, label: account.name, countries: account.countries, sows: account.sows })),
              selected: selectedAccounts
            }
          ]}
          selectedFilters={filters}
          onFilterChange={handleFilterChange}
          searchLogs={fetchLogs}
          handleExport={handleExport}
          exportButtonText={exportButtonText}
          exportMode={exportMode}
          logsCount={totalCount}
          selectedSows={selectedSows}
        />
        <div className="mt-2" />
        <div>
        <div>
          <DataTable
            columns={columns}
            data={logs}
            useBackendSorting={true}
          />

          <div className="grid grid-cols-8 items-center mt-4 border-t-2 border-border pt-2">
            <div className="col-span-1 font-semibold">Total Duration:</div>
            <div className="col-span-4"></div>
            <div className="col-span-1 text-right">
              {totalDuration}
            </div>
            <div className="col-span-2"></div>
          </div>
        </div>
    

        <Sheet open={isEditOpen} onOpenChange={setIsEditOpen}>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>Edit Log {selectedLogFormVariables?.log?.id}</SheetTitle>
            </SheetHeader>
            <EditLog
              logCategories={selectedLogFormVariables?.logCategories}
              internalSubTicketTypes={selectedLogFormVariables?.internalSubTicketTypes}
              users={users}
              currentLog={selectedLogFormVariables?.log}
              onClose={() => setIsEditOpen(false)}
              onSuccess={fetchLogs}
            />
          </SheetContent>
        </Sheet>

        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={pagination.per_page}
          onPageChange={handlePageChange}
        />
        </div>
      </Container>
    </>
  );
};

export default Index;
